import { useState, useEffect, useMemo, useCallback, useReducer, createContext, useContext } from 'react';
import { 
  ShoppingCart, Plus, Minus, Search, MapPin, Clock, CheckCircle, User, 
  Bell, CreditCard, Package, Pill, Calendar, Star, Phone, Home, Truck, 
  MessageCircle, AlertCircle, FileText, RefreshCw, Shield, Database, 
  Settings, ChevronRight, X, Menu, ChevronDown, ArrowRight, Check, Info, 
  QrCode, Filter, Heart, History, LogOut, Edit, Camera, Upload, Download,
  TrendingUp, Award, Gift, Zap, AlertTriangle, Navigation
} from 'lucide-react';

// ============= CONTEXT & STATE MANAGEMENT =============
const AppContext = createContext();

const initialState = {
  user: {
    name: "Sarah Johnson",
    email: "sarah.johnson@email.com",
    phone: "+61 4xx xxx xxx",
    medicare: "2736 41283 2",
    address: "42 Wallaby Way, Melbourne VIC 3000",
    hasAccount: true,
    insurance: 'BlueCross BlueShield',
    concessionCard: 'Healthcare Card',
    demographics: { age: 45, gender: 'Female' },
    pbsSafetyNet: {
      currentSpend: 832.50,
      threshold: 1694.00,
      percentage: 49.1,
      remainingToThreshold: 861.50
    },
    preferences: {
      notifications: { sms: true, email: true, push: true },
      autoRefill: true,
      defaultPharmacy: null
    }
  },
  cart: [],
  favorites: [],
  notifications: [],
  activeTab: 'home',
  searchQuery: '',
  selectedPharmacy: null,
  filters: {
    categories: [],
    priceRange: [0, 100],
    prescription: null,
    pbs: null,
    inStock: null
  }
};

function appReducer(state, action) {
  switch (action.type) {
    case 'ADD_TO_CART':
      const existingItem = state.cart.find(item => item.id === action.payload.id);
      if (existingItem) {
        return {
          ...state,
          cart: state.cart.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        };
      }
      return {
        ...state,
        cart: [...state.cart, { ...action.payload, quantity: 1 }]
      };
    
    case 'UPDATE_CART_QUANTITY':
      if (action.payload.quantity === 0) {
        return {
          ...state,
          cart: state.cart.filter(item => item.id !== action.payload.id)
        };
      }
      return {
        ...state,
        cart: state.cart.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        )
      };
    
    case 'REMOVE_FROM_CART':
      return {
        ...state,
        cart: state.cart.filter(item => item.id !== action.payload)
      };
    
    case 'CLEAR_CART':
      return { ...state, cart: [] };
    
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };
    
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };
    
    case 'TOGGLE_FAVORITE':
      const isFavorite = state.favorites.includes(action.payload);
      return {
        ...state,
        favorites: isFavorite
          ? state.favorites.filter(id => id !== action.payload)
          : [...state.favorites, action.payload]
      };
    
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications]
      };
    
    case 'MARK_NOTIFICATION_READ':
      return {
        ...state,
        notifications: state.notifications.map(notif =>
          notif.id === action.payload ? { ...notif, read: true } : notif
        )
      };
    
    case 'SET_SELECTED_PHARMACY':
      return { ...state, selectedPharmacy: action.payload };
    
    case 'UPDATE_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload }
      };
    
    case 'UPDATE_USER_PREFERENCES':
      return {
        ...state,
        user: {
          ...state.user,
          preferences: { ...state.user.preferences, ...action.payload }
        }
      };
    
    default:
      return state;
  }
}

// ============= CUSTOM HOOKS =============
const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppContext must be used within AppProvider');
  return context;
};

const useGeolocation = () => {
  const [location, setLocation] = useState({ lat: -37.8136, lng: 144.9631 });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported');
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setLoading(false);
      },
      (error) => {
        setError(error.message);
        setLoading(false);
      }
    );
  }, []);

  return { location, error, loading };
};

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// ============= DATA & UTILITIES =============
const medications = [
  { 
    id: 1, 
    name: 'Metformin 500mg', 
    brandName: 'Glucophage',
    price: 12.99, 
    pbsPrice: 6.80,
    category: 'Diabetes', 
    inStock: true, 
    stockLevel: 45,
    prescription: true, 
    description: 'Oral medication used to treat type 2 diabetes by improving blood sugar control',
    tgaApproved: true,
    pbs: true,
    safetyLevel: 'Low risk',
    dosage: '1 tablet twice daily with meals',
    sideEffects: 'Nausea, diarrhea, stomach upset',
    interactions: 'May interact with certain heart medications and contrast dyes',
    image: 'https://placehold.co/100x100/e0f2fe/3b82f6?text=Met',
    rating: 4.5,
    reviews: 128
  },
  { 
    id: 2, 
    name: 'Lisinopril 10mg', 
    brandName: 'Zestril',
    price: 8.50, 
    pbsPrice: 6.80,
    category: 'Blood Pressure', 
    inStock: true, 
    stockLevel: 12,
    prescription: true, 
    description: 'ACE inhibitor medication used to treat high blood pressure and heart failure',
    tgaApproved: true,
    pbs: true,
    safetyLevel: 'Medium risk',
    dosage: '1 tablet daily',
    sideEffects: 'Dry cough, dizziness, headache',
    interactions: 'May interact with potassium supplements and NSAIDs',
    image: 'https://placehold.co/100x100/e0f2fe/3b82f6?text=Lis',
    rating: 4.3,
    reviews: 87
  },
  { 
    id: 3, 
    name: 'Ibuprofen 200mg', 
    brandName: 'Nurofen',
    price: 6.99, 
    pbsPrice: null,
    category: 'Pain Relief', 
    inStock: true, 
    stockLevel: 89,
    prescription: false, 
    description: 'Non-steroidal anti-inflammatory drug (NSAID) for pain relief and reducing inflammation',
    tgaApproved: true,
    pbs: false,
    safetyLevel: 'Low risk',
    dosage: '1-2 tablets every 4-6 hours as needed',
    sideEffects: 'Stomach upset, heartburn, dizziness',
    interactions: 'May interact with blood thinners and certain blood pressure medications',
    image: 'https://placehold.co/100x100/e0f2fe/3b82f6?text=Ibu',
    rating: 4.7,
    reviews: 234
  },
  { 
    id: 4, 
    name: 'Atorvastatin 20mg', 
    brandName: 'Lipitor',
    price: 15.75, 
    pbsPrice: 6.80,
    category: 'Cholesterol', 
    inStock: false, 
    stockLevel: 0,
    prescription: true, 
    description: 'Statin medication used to lower blood cholesterol levels and reduce risk of heart disease',
    tgaApproved: true,
    pbs: true,
    safetyLevel: 'Medium risk',
    estimatedRestock: '2025-07-25',
    dosage: '1 tablet daily in the evening',
    sideEffects: 'Muscle pain, weakness, liver problems',
    interactions: 'May interact with grapefruit juice and certain antibiotics',
    image: 'https://placehold.co/100x100/e0f2fe/3b82f6?text=Ato',
    rating: 4.4,
    reviews: 156
  },
  { 
    id: 5, 
    name: 'Sertraline 50mg', 
    brandName: 'Zoloft',
    price: 9.99, 
    pbsPrice: 6.80,
    category: 'Mental Health', 
    inStock: true, 
    stockLevel: 32,
    prescription: true, 
    description: 'Selective serotonin reuptake inhibitor (SSRI) used to treat depression and anxiety disorders',
    tgaApproved: true,
    pbs: true,
    safetyLevel: 'Medium risk',
    dosage: '1 tablet daily with food',
    sideEffects: 'Nausea, insomnia, dizziness',
    interactions: 'May interact with MAOIs and certain pain medications',
    image: 'https://placehold.co/100x100/e0f2fe/3b82f6?text=Ser',
    rating: 4.2,
    reviews: 98
  },
  { 
    id: 6, 
    name: 'Ventolin Inhaler 100mcg', 
    brandName: 'Salbutamol',
    price: 14.50, 
    pbsPrice: 6.80,
    category: 'Respiratory', 
    inStock: true, 
    stockLevel: 25,
    prescription: false, 
    description: 'Bronchodilator used to relieve symptoms of asthma and chronic obstructive pulmonary disease (COPD)',
    tgaApproved: true,
    pbs: true,
    safetyLevel: 'Low risk',
    dosage: '1-2 puffs every 4-6 hours as needed',
    sideEffects: 'Tremor, increased heart rate, headache',
    interactions: 'May interact with certain blood pressure medications',
    image: 'https://placehold.co/100x100/e0f2fe/3b82f6?text=Ven',
    rating: 4.8,
    reviews: 312
  }
];

// Complementary Products Data with enhanced images
const complementaryProducts = [
  // Vitamins & Supplements
  { id: 101, name: 'Vitamin D3 1000IU', price: 15.99, image: 'https://placehold.co/200x200/fef3c7/f59e0b?text=Vitamin+D3', category: 'Vitamins', relatedTo: ['Mental Health', 'Diabetes'], demographics: { age: [30, 99] }, description: 'Supports bone health and immune function' },
  { id: 102, name: 'Vitamin B12 Complex', price: 18.50, image: 'https://placehold.co/200x200/ddd6fe/8b5cf6?text=B12+Complex', category: 'Vitamins', relatedTo: ['Mental Health'], demographics: { age: [40, 99] }, description: 'Boosts energy and brain function' },
  { id: 103, name: 'Calcium + Vitamin D', price: 14.99, image: 'https://placehold.co/200x200/e0e7ff/6366f1?text=Calcium', category: 'Vitamins', relatedTo: ['Blood Pressure'], demographics: { age: [45, 99], gender: 'Female' }, description: 'Essential for strong bones' },
  { id: 104, name: 'Omega-3 Fish Oil', price: 22.99, image: 'https://placehold.co/200x200/cffafe/06b6d4?text=Omega-3', category: 'Vitamins', relatedTo: ['Cholesterol', 'Blood Pressure'], demographics: { age: [35, 99] }, description: 'Heart and brain health support' },
  { id: 105, name: 'Multivitamin 50+', price: 24.99, image: 'https://placehold.co/200x200/f3e8ff/a855f7?text=Multi+50%2B', category: 'Vitamins', demographics: { age: [50, 99] }, description: 'Complete daily nutrition for seniors' },
  { id: 106, name: 'Magnesium 400mg', price: 12.99, image: 'https://placehold.co/200x200/ecfccb/84cc16?text=Magnesium', category: 'Vitamins', relatedTo: ['Blood Pressure', 'Diabetes'], demographics: { age: [30, 99] }, description: 'Muscle and nerve support' },
  
  // General Health
  { id: 201, name: 'Hand Sanitizer 250ml', price: 4.50, image: 'https://placehold.co/200x200/e0f2fe/0ea5e9?text=Sanitizer', category: 'General Health', description: 'Kills 99.9% of germs' },
  { id: 202, name: 'Face Masks (50 pack)', price: 19.99, image: 'https://placehold.co/200x200/f0f9ff/3b82f6?text=Face+Masks', category: 'General Health', description: '3-layer protective masks' },
  { id: 203, name: 'Antiseptic Cream 50g', price: 5.50, image: 'https://placehold.co/200x200/fef2f2/ef4444?text=Antiseptic', category: 'First Aid', relatedTo: ['Pain Relief'], description: 'Prevents infection in cuts' },
  { id: 204, name: 'Digital Thermometer', price: 12.00, image: 'https://placehold.co/200x200/f5f5f4/78716c?text=Thermometer', category: 'Health Monitoring', description: 'Fast accurate readings' },
  { id: 205, name: 'First Aid Kit Complete', price: 32.99, image: 'https://placehold.co/200x200/fee2e2/dc2626?text=First+Aid', category: 'First Aid', description: 'Everything for emergencies' },
  
  // Pain & Recovery
  { id: 301, name: 'Hot/Cold Pack Reusable', price: 9.75, image: 'https://placehold.co/200x200/dbeafe/3b82f6?text=Hot%2FCold', category: 'Pain Relief', relatedTo: ['Pain Relief'], description: 'Instant pain relief' },
  { id: 302, name: 'Elastic Bandage 10cm', price: 6.99, image: 'https://placehold.co/200x200/fef3c7/f59e0b?text=Bandage', category: 'First Aid', relatedTo: ['Pain Relief'], description: 'Compression support' },
  { id: 303, name: 'Joint Support Cream', price: 14.50, image: 'https://placehold.co/200x200/d1fae5/10b981?text=Joint+Cream', category: 'Pain Relief', demographics: { age: [40, 99] }, description: 'Soothing joint relief' },
  
  // Diabetes Care
  { id: 401, name: 'Glucose Test Strips (50)', price: 25.50, image: 'https://placehold.co/200x200/ede9fe/8b5cf6?text=Test+Strips', category: 'Diabetes Care', relatedTo: ['Diabetes'], description: 'Accurate blood glucose testing' },
  { id: 402, name: 'Blood Glucose Monitor', price: 45.00, image: 'https://placehold.co/200x200/e9d5ff/9333ea?text=Glucose+Monitor', category: 'Diabetes Care', relatedTo: ['Diabetes'], description: 'Easy-to-use monitor' },
  { id: 403, name: 'Diabetic Socks (3 pairs)', price: 18.99, image: 'https://placehold.co/200x200/f3e8ff/a855f7?text=Diabetic+Socks', category: 'Diabetes Care', relatedTo: ['Diabetes'], description: 'Comfort and protection' },
  { id: 404, name: 'Sugar-Free Lozenges', price: 5.99, image: 'https://placehold.co/200x200/dcfce7/65a30d?text=SF+Lozenges', category: 'Diabetes Care', relatedTo: ['Diabetes', 'Respiratory'], description: 'Throat relief without sugar' },
  
  // Heart Health
  { id: 501, name: 'Blood Pressure Monitor', price: 65.00, image: 'https://placehold.co/200x200/fee2e2/dc2626?text=BP+Monitor', category: 'Heart Health', relatedTo: ['Blood Pressure', 'Cholesterol'], description: 'Clinical accuracy at home' },
  { id: 502, name: 'CoQ10 100mg', price: 28.99, image: 'https://placehold.co/200x200/fce7f3/ec4899?text=CoQ10', category: 'Vitamins', relatedTo: ['Cholesterol', 'Blood Pressure'], demographics: { age: [40, 99] }, description: 'Heart health antioxidant' },
  { id: 503, name: 'Low Sodium Salt', price: 4.50, image: 'https://placehold.co/200x200/e7e5e4/78716c?text=Low+Salt', category: 'Heart Health', relatedTo: ['Blood Pressure'], description: 'Heart-friendly alternative' },
  
  // Respiratory
  { id: 601, name: 'Saline Nasal Spray 30ml', price: 7.20, image: 'https://placehold.co/200x200/e0f2fe/0284c7?text=Nasal+Spray', category: 'Respiratory', relatedTo: ['Respiratory'], description: 'Natural congestion relief' },
  { id: 602, name: 'Eucalyptus Oil 10ml', price: 8.99, image: 'https://placehold.co/200x200/d1fae5/059669?text=Eucalyptus', category: 'Respiratory', relatedTo: ['Respiratory'], description: 'Clear breathing naturally' },
  { id: 603, name: 'Spacer Device', price: 15.00, image: 'https://placehold.co/200x200/dbeafe/2563eb?text=Spacer', category: 'Respiratory', relatedTo: ['Respiratory'], description: 'Better inhaler delivery' },
  
  // Digestive Health
  { id: 701, name: 'Probiotic Capsules (30)', price: 22.00, image: 'https://placehold.co/200x200/fef3c7/ea580c?text=Probiotics', category: 'Digestive', relatedTo: ['Mental Health', 'Diabetes'], description: 'Gut health support' },
  { id: 702, name: 'Antacid Tablets (100)', price: 9.99, image: 'https://placehold.co/200x200/fed7aa/f97316?text=Antacid', category: 'Digestive', relatedTo: ['Pain Relief'], description: 'Fast heartburn relief' },
  { id: 703, name: 'Fiber Supplement', price: 16.50, image: 'https://placehold.co/200x200/ccfbf1/14b8a6?text=Fiber', category: 'Digestive', relatedTo: ['Diabetes', 'Cholesterol'], demographics: { age: [35, 99] }, description: 'Digestive regularity' },
  
  // Mental Wellness
  { id: 801, name: 'Sleep Aid Melatonin', price: 13.99, image: 'https://placehold.co/200x200/e9d5ff/7c3aed?text=Melatonin', category: 'Mental Wellness', relatedTo: ['Mental Health'], description: 'Natural sleep support' },
  { id: 802, name: 'Stress Relief Tea', price: 8.50, image: 'https://placehold.co/200x200/dcfce7/4ade80?text=Calm+Tea', category: 'Mental Wellness', relatedTo: ['Mental Health', 'Blood Pressure'], description: 'Relaxing herbal blend' },
  { id: 803, name: 'Essential Oil Diffuser', price: 35.00, image: 'https://placehold.co/200x200/f3e8ff/9333ea?text=Diffuser', category: 'Mental Wellness', relatedTo: ['Mental Health'], description: 'Aromatherapy wellness' }
];

const pharmacies = [
  { 
    id: 1, 
    name: 'MediCare Pharmacy', 
    address: '123 Health Street, Melbourne',
    location: { lat: -37.8140, lng: 144.9635 },
    rating: 4.8, 
    tgaConnected: true,
    smsEnabled: true,
    openingHours: '8:00 AM - 8:00 PM',
    phoneNumber: '03 9123 4567',
    image: 'https://placehold.co/60x60/dbeafe/1e40af?text=MC',
    services: ['eScript', 'Vaccination', 'Health Checks', 'NDSS'],
    stock: { 
      1: 50, 2: 20, 3: 100, 5: 15, 6: 30,
      // Complementary products stock
      101: 50, 102: 30, 103: 25, 104: 40, 105: 20, 106: 35,
      201: 100, 202: 80, 203: 60, 204: 15, 205: 10,
      301: 40, 302: 50, 303: 25,
      401: 30, 402: 5, 403: 20, 404: 45,
      501: 8, 502: 15, 503: 50,
      601: 40, 602: 25, 603: 10,
      701: 25, 702: 35, 703: 20,
      801: 30, 802: 40, 803: 5
    }
  },
  { 
    id: 2, 
    name: 'Health Plus Drugs', 
    address: '456 Wellness Avenue, Melbourne',
    location: { lat: -37.8156, lng: 144.9651 },
    rating: 4.6, 
    tgaConnected: true,
    smsEnabled: true,
    openingHours: '8:30 AM - 7:00 PM',
    phoneNumber: '03 9234 5678',
    image: 'https://placehold.co/60x60/dbeafe/1e40af?text=HP',
    services: ['eScript', 'Vaccination', 'Compounding'],
    stock: { 
      1: 10, 3: 50, 4: 0, 5: 5, 6: 10,
      // Complementary products stock
      101: 20, 102: 15, 104: 10, 106: 25,
      201: 50, 202: 30, 203: 40, 204: 10,
      301: 20, 302: 30,
      401: 15, 404: 30,
      501: 3, 503: 30,
      601: 20, 602: 15,
      701: 10, 702: 20,
      801: 15, 802: 25
    }
  },
  { 
    id: 3, 
    name: 'Central City Pharmacy', 
    address: '789 Main Street, Melbourne',
    location: { lat: -37.8116, lng: 144.9681 },
    rating: 4.9, 
    tgaConnected: true,
    smsEnabled: true,
    openingHours: '7:00 AM - 10:00 PM',
    phoneNumber: '03 9345 6789',
    image: 'https://placehold.co/60x60/dbeafe/1e40af?text=CC',
    services: ['eScript', 'Vaccination', 'Health Checks', 'NDSS', '24/7 Support'],
    stock: { 
      1: 100, 2: 50, 3: 200, 4: 5, 5: 50, 6: 50,
      // Complementary products stock
      101: 100, 102: 50, 103: 40, 104: 60, 105: 30, 106: 55,
      201: 150, 202: 100, 203: 80, 204: 25, 205: 15,
      301: 60, 302: 70, 303: 35,
      401: 40, 402: 10, 403: 30, 404: 60,
      501: 12, 502: 20, 503: 80,
      601: 60, 602: 35, 603: 15,
      701: 35, 702: 50, 703: 30,
      801: 40, 802: 60, 803: 8
    }
  }
];

const prescriptions = [
  {
    id: 'RX001',
    medication: 'Metformin 500mg',
    doctor: 'Dr. Sarah Smith',
    issued: '2025-06-01',
    expires: '2025-08-01',
    refillsRemaining: 2,
    totalRefills: 5,
    status: 'Active',
    isRepeat: true,
    nextReminderDate: '2025-07-25',
    pbs: true,
    brandOptions: ['Glucophage', 'Diabex', 'Diaformin']
  },
  {
    id: 'RX002',
    medication: 'Lisinopril 10mg',
    doctor: 'Dr. John Johnson',
    issued: '2025-05-15',
    expires: '2025-07-26',
    refillsRemaining: 1,
    totalRefills: 3,
    status: 'Expiring Soon',
    isRepeat: true,
    nextReminderDate: '2025-07-24',
    pbs: true,
    brandOptions: ['Zestril', 'Prinivil', 'Lisodur']
  },
  {
    id: 'RX003',
    medication: 'Sertraline 50mg',
    doctor: 'Dr. Emily Chen',
    issued: '2025-07-01',
    expires: '2025-10-01',
    refillsRemaining: 3,
    totalRefills: 5,
    status: 'Active',
    isRepeat: true,
    nextReminderDate: '2025-07-29',
    pbs: true,
    brandOptions: ['Zoloft', 'Setrona', 'Sertralin']
  }
];

const healthTips = [
  {
    id: 1,
    title: "Managing Type 2 Diabetes",
    content: "Regular blood sugar monitoring, healthy diet, and exercise are key to managing diabetes effectively.",
    category: "Diabetes",
    icon: TrendingUp
  },
  {
    id: 2,
    title: "Blood Pressure Control",
    content: "Reduce salt intake, maintain healthy weight, and take medications as prescribed for optimal blood pressure.",
    category: "Blood Pressure",
    icon: Heart
  },
  {
    id: 3,
    title: "Mental Health Awareness",
    content: "Stay connected with loved ones, practice mindfulness, and seek professional help when needed.",
    category: "Mental Health",
    icon: Shield
  }
];

// Utility Functions
const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d.toFixed(1);
};

const deg2rad = (deg) => deg * (Math.PI / 180);

const formatCurrency = (amount) => `$${amount.toFixed(2)}`;

const calculateDeliveryTime = (distance) => {
  const baseTime = 30;
  const timePerKm = 5;
  return Math.round(baseTime + (parseFloat(distance) * timePerKm));
};

// Smart complementary product suggestion algorithm with dynamic replacement
const getComplementaryProducts = (cart, pharmacy, userProfile, excludedIds = []) => {
  const suggestions = new Map();
  const cartCategories = cart.length > 0 
    ? [...new Set(cart.map(item => medications.find(m => m.id === item.id)?.category))]
    : [];
  const userAge = userProfile.demographics.age;
  const userGender = userProfile.demographics.gender;
  
  // Score each complementary product
  complementaryProducts.forEach(product => {
    let score = 0;
    const productId = product.id;
    
    // Skip if already in cart or excluded
    if (excludedIds.includes(productId)) return;
    if (cart.some(item => item.id === productId)) return;
    
    // Check if product is in stock at selected pharmacy
    if (!pharmacy.stock[productId] || pharmacy.stock[productId] === 0) return;
    
    // 1. Category relationship scoring (highest priority)
    if (product.relatedTo && cart.length > 0) {
      cartCategories.forEach(category => {
        if (product.relatedTo.includes(category)) {
          score += 30; // High score for direct category relationship
        }
      });
    }
    
    // 2. Demographics scoring
    if (product.demographics) {
      // Age matching
      if (product.demographics.age && 
          userAge >= product.demographics.age[0] && 
          userAge <= product.demographics.age[1]) {
        score += 20;
      }
      
      // Gender matching
      if (product.demographics.gender && product.demographics.gender === userGender) {
        score += 15;
      }
    }
    
    // 3. Common health conditions associations
    const conditionAssociations = {
      'Diabetes': ['Blood Pressure', 'Cholesterol', 'Heart Health'],
      'Blood Pressure': ['Cholesterol', 'Heart Health', 'Diabetes'],
      'Mental Health': ['Digestive', 'Pain Relief'],
      'Cholesterol': ['Blood Pressure', 'Heart Health'],
      'Respiratory': ['General Health']
    };
    
    cartCategories.forEach(cartCat => {
      const associations = conditionAssociations[cartCat] || [];
      if (associations.includes(product.category)) {
        score += 10;
      }
    });
    
    // 4. General wellness products for older adults
    if (userAge >= 50 && ['Vitamins', 'Heart Health', 'Digestive'].includes(product.category)) {
      score += 5;
    }
    
    // 5. Essential items everyone needs
    if (['General Health', 'First Aid'].includes(product.category)) {
      score += 3;
    }
    
    // Store product with score if it has any relevance
    if (score > 0 || cart.length === 0) {
      suggestions.set(productId, { ...product, score, stockLevel: pharmacy.stock[productId] });
    }
  });
  
  // If no cart items, show general wellness products
  if (cart.length === 0) {
    complementaryProducts.forEach(product => {
      if (!excludedIds.includes(product.id) && pharmacy.stock[product.id] > 0) {
        const score = product.demographics && product.demographics.age && 
          userAge >= product.demographics.age[0] && 
          userAge <= product.demographics.age[1] ? 10 : 5;
        suggestions.set(product.id, { ...product, score, stockLevel: pharmacy.stock[product.id] });
      }
    });
  }
  
  // Sort by score and return top suggestions
  return Array.from(suggestions.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, 8); // Return top 8 suggestions
};

// ============= COMPONENTS =============
const Header = ({ state, dispatch }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const unreadCount = state.notifications.filter(n => !n.read).length;
  const cartCount = state.cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 z-20 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-gray-600"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex items-center space-x-1">
            <h1 className="text-xl font-bold text-blue-600">MedFast</h1>
            <Database className="h-5 w-5 text-green-500" title="TGA Connected" />
            <span className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full ml-1">PRO</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => dispatch({ type: 'SET_ACTIVE_TAB', payload: 'orders' })}
            className="hidden md:flex items-center text-gray-600 hover:text-gray-900"
          >
            <Navigation className="h-5 w-5 mr-1" />
            <span className="text-sm">Track Order</span>
          </button>
          
          <div className="relative">
            <button 
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-1 rounded-full hover:bg-gray-100"
            >
              <Bell className="h-6 w-6 text-gray-600" />
              {unreadCount > 0 && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">{unreadCount}</span>
                </div>
              )}
            </button>
            
            {showNotifications && (
              <NotificationPanel 
                notifications={state.notifications}
                onClose={() => setShowNotifications(false)}
                dispatch={dispatch}
              />
            )}
          </div>
          
          <button 
            onClick={() => dispatch({ type: 'SET_ACTIVE_TAB', payload: 'cart' })}
            className="relative p-1 rounded-full hover:bg-gray-100"
          >
            <ShoppingCart className="h-6 w-6 text-gray-600" />
            {cartCount > 0 && (
              <div className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {cartCount}
              </div>
            )}
          </button>
          
          <button
            onClick={() => dispatch({ type: 'SET_ACTIVE_TAB', payload: 'profile' })}
            className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center hover:bg-blue-200 transition"
          >
            <span className="font-medium text-sm">{state.user.name.split(' ').map(n => n[0]).join('')}</span>
          </button>
        </div>
      </div>
      
      {mobileMenuOpen && (
        <MobileMenu 
          activeTab={state.activeTab}
          dispatch={dispatch}
          onClose={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

const NotificationPanel = ({ notifications, onClose, dispatch }) => (
  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
    <div className="p-3 border-b border-gray-200">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Notifications</h3>
        <button className="text-sm text-blue-600">Mark all read</button>
      </div>
    </div>
    <div className="max-h-96 overflow-y-auto">
      {notifications.length > 0 ? (
        notifications.map(notif => (
          <NotificationItem 
            key={notif.id} 
            notification={notif}
            dispatch={dispatch}
            onClose={onClose}
          />
        ))
      ) : (
        <div className="p-4 text-center text-gray-500">
          <Bell className="h-12 w-12 text-gray-300 mx-auto mb-2" />
          <p>No notifications</p>
        </div>
      )}
    </div>
  </div>
);

const NotificationItem = ({ notification, dispatch, onClose }) => {
  const handleAction = () => {
    dispatch({ type: 'MARK_NOTIFICATION_READ', payload: notification.id });
    
    if (notification.type === 'prescription' && notification.prescriptionId) {
      dispatch({ type: 'SET_ACTIVE_TAB', payload: 'prescriptions' });
    } else if (notification.type === 'order') {
      dispatch({ type: 'SET_ACTIVE_TAB', payload: 'orders' });
    }
    
    onClose();
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'sms': return <MessageCircle className="h-5 w-5" />;
      case 'reminder': return <Clock className="h-5 w-5" />;
      case 'ready': return <CheckCircle className="h-5 w-5" />;
      case 'delivery': return <Truck className="h-5 w-5" />;
      default: return <Info className="h-5 w-5" />;
    }
  };

  const getColorClass = () => {
    switch (notification.type) {
      case 'sms': return 'bg-green-100 text-green-600';
      case 'reminder': return 'bg-orange-100 text-orange-600';
      case 'ready': return 'bg-blue-100 text-blue-600';
      case 'delivery': return 'bg-purple-100 text-purple-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div 
      className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${!notification.read ? 'bg-blue-50' : 'bg-white'}`}
      onClick={handleAction}
    >
      <div className="flex items-start">
        <div className={`rounded-full p-2 mr-3 ${getColorClass()}`}>
          {getIcon()}
        </div>
        <div className="flex-1">
          <p className="font-medium text-sm">{notification.title}</p>
          <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs text-gray-500">{notification.time}</span>
            {notification.action && (
              <button className="text-xs text-blue-600 font-medium">{notification.action}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const MobileMenu = ({ activeTab, dispatch, onClose }) => {
  const menuItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'search', label: 'My Medications', icon: Pill },
    { id: 'prescriptions', label: 'My Prescriptions', icon: FileText },
    { id: 'orders', label: 'Orders', icon: Package },
    { id: 'favorites', label: 'Favorites', icon: Heart },
    { id: 'cart', label: 'Cart', icon: ShoppingCart },
    { id: 'profile', label: 'My Profile', icon: User }
  ];

  return (
    <div className="md:hidden absolute top-16 left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-20">
      <div className="py-2">
        {menuItems.map(item => (
          <button
            key={item.id}
            onClick={() => {
              dispatch({ type: 'SET_ACTIVE_TAB', payload: item.id });
              onClose();
            }}
            className={`flex items-center w-full px-4 py-3 ${
              activeTab === item.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
            }`}
          >
            <item.icon className="h-5 w-5 mr-3" />
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const BottomNavigation = ({ activeTab, dispatch }) => {
  const tabs = [
    { id: 'home', icon: Home, label: 'Home' },
    { id: 'search', icon: Pill, label: 'Medications' },
    { id: 'prescriptions', icon: FileText, label: 'RX' },
    { id: 'cart', icon: ShoppingCart, label: 'Cart' },
    { id: 'profile', icon: User, label: 'Profile' }
  ];

  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-10">
      <div className="flex justify-around py-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => dispatch({ type: 'SET_ACTIVE_TAB', payload: tab.id })}
            className={`flex flex-col items-center py-2 px-4 relative ${
              activeTab === tab.id ? 'text-blue-600' : 'text-gray-500'
            }`}
          >
            <tab.icon className="h-5 w-5" />
            <span className="text-xs mt-1">{tab.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-8">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

const ErrorMessage = ({ message, onRetry }) => (
  <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
    <div className="flex items-center">
      <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
      <p className="text-red-800">{message}</p>
    </div>
    {onRetry && (
      <button 
        onClick={onRetry}
        className="mt-2 text-sm text-red-600 hover:text-red-700"
      >
        Try again
      </button>
    )}
  </div>
);

const MedicationCard = ({ medication, dispatch, isFavorite }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex">
        <div className="w-16 h-16 bg-gray-100 rounded-lg mr-3 flex items-center justify-center overflow-hidden">
          <img src={medication.image} alt={medication.name} className="w-full h-full object-cover" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-gray-900">{medication.name}</h3>
            <button
              onClick={() => dispatch({ type: 'TOGGLE_FAVORITE', payload: medication.id })}
              className={`p-1 rounded-full hover:bg-gray-100 ${isFavorite ? 'text-red-500' : 'text-gray-400'}`}
            >
              <Heart className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''}`} />
            </button>
          </div>
          
          {medication.brandName && (
            <p className="text-xs text-gray-500 mb-1">Brand: {medication.brandName}</p>
          )}
          
          <div className="flex items-center space-x-1 mb-1">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  className={`h-3 w-3 ${i < Math.floor(medication.rating) ? 'fill-current' : ''}`} 
                />
              ))}
            </div>
            <span className="text-xs text-gray-600">({medication.reviews})</span>
          </div>
          
          <p className="text-sm text-gray-600 mb-2 line-clamp-2">{medication.description}</p>
          
          <div className="flex items-center space-x-2 flex-wrap gap-1">
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
              {medication.category}
            </span>
            {medication.prescription && (
              <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                Prescription
              </span>
            )}
            {medication.pbs && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                PBS
              </span>
            )}
            {medication.tgaApproved && (
              <Shield className="h-4 w-4 text-green-500" />
            )}
          </div>
        </div>
        
        <div className="text-right ml-4">
          <div className="mb-2">
            {medication.pbs && medication.pbsPrice ? (
              <>
                <p className="text-lg font-bold text-gray-900">{formatCurrency(medication.pbsPrice)}</p>
                <p className="text-xs text-gray-500 line-through">RRP {formatCurrency(medication.price)}</p>
                <p className="text-xs text-green-600">PBS Price</p>
              </>
            ) : (
              <p className="text-lg font-bold text-gray-900">{formatCurrency(medication.price)}</p>
            )}
          </div>
          
          <div className={`text-sm ${medication.inStock ? 'text-green-600' : 'text-red-600'}`}>
            {medication.inStock ? (
              <div>
                <p className="flex items-center">
                  <Check className="h-3 w-3 mr-1" />
                  In Stock
                </p>
                {medication.stockLevel < 20 && <p className="text-xs text-orange-600">Low stock</p>}
              </div>
            ) : (
              <div>
                <p>Out of Stock</p>
                {medication.estimatedRestock && (
                  <p className="text-xs text-gray-500">Restock: {medication.estimatedRestock}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="mt-4 flex space-x-2">
        <button
          onClick={() => dispatch({ type: 'ADD_TO_CART', payload: medication })}
          disabled={!medication.inStock || (medication.prescription && medication.prescription.refillsRemaining === 0)}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            medication.inStock && (!medication.prescription || medication.prescription.refillsRemaining > 0)
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {!medication.inStock ? 'Out of Stock' : 
           medication.prescription && medication.prescription.refillsRemaining === 0 ? 'No Refills Available' :
           'Add to Cart'}
        </button>
        <button 
          onClick={() => setShowDetails(!showDetails)}
          className="py-2 px-3 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Info className="h-5 w-5 text-gray-600" />
        </button>
      </div>
      
      {showDetails && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
          <div className="space-y-2">
            <div>
              <span className="font-medium">Dosage:</span> {medication.dosage}
            </div>
            <div>
              <span className="font-medium">Side Effects:</span> {medication.sideEffects}
            </div>
            <div>
              <span className="font-medium">Interactions:</span> {medication.interactions}
            </div>
            <div>
              <span className="font-medium">Safety Level:</span> 
              <span className={`ml-1 px-2 py-1 rounded-full text-xs ${
                medication.safetyLevel === 'Low risk' ? 'bg-green-100 text-green-800' :
                medication.safetyLevel === 'Medium risk' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {medication.safetyLevel}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ComplementaryProductsSection = ({ pharmacy, userProfile, dispatch, cartItems }) => {
  const [excludedIds, setExcludedIds] = useState([]);
  const [animatingIds, setAnimatingIds] = useState([]);
  
  const suggestions = useMemo(() => {
    return getComplementaryProducts(cartItems, pharmacy, userProfile, excludedIds);
  }, [cartItems, pharmacy, userProfile, excludedIds]);

  const handleAddToCart = (item) => {
    // Add animation
    setAnimatingIds([...animatingIds, item.id]);
    
    // Add to cart
    dispatch({ type: 'ADD_TO_CART', payload: item });
    
    // After animation, exclude this item
    setTimeout(() => {
      setExcludedIds([...excludedIds, item.id]);
      setAnimatingIds(animatingIds.filter(id => id !== item.id));
    }, 300);
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-800 flex items-center">
            <Gift className="h-6 w-6 mr-2 text-purple-600" />
            Recommended for You
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Personalized health essentials available at {pharmacy.name}
          </p>
        </div>
        <span className="text-xs bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium">
          In Stock Now
        </span>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {suggestions.slice(0, 4).map(item => (
          <div 
            key={item.id} 
            className={`bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden transform ${
              animatingIds.includes(item.id) ? 'scale-0 opacity-0' : 'scale-100 opacity-100 hover:-translate-y-1'
            }`}
          >
            <div className="relative">
              {item.score >= 40 && (
                <div className="absolute top-2 right-2 z-10">
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs px-2 py-1 rounded-full font-medium shadow-md">
                    Best Match
                  </span>
                </div>
              )}
              <img 
                src={item.image} 
                alt={item.name} 
                className="w-full h-32 object-cover hover:scale-105 transition-transform duration-200"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-2">
                <span className="text-white text-xs font-medium">
                  {item.category}
                </span>
              </div>
            </div>
            
            <div className="p-4">
              <h4 className="font-semibold text-sm text-gray-800 line-clamp-1 mb-1">
                {item.name}
              </h4>
              <p className="text-xs text-gray-600 line-clamp-2 mb-3 h-8">
                {item.description}
              </p>
              
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-bold text-gray-900">
                  {formatCurrency(item.price)}
                </span>
                <span className="text-xs text-gray-500">
                  Stock: {item.stockLevel}
                </span>
              </div>
              
              <button 
                onClick={() => handleAddToCart(item)}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium py-2 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 transform hover:scale-105"
              >
                Add to Cart
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {suggestions.length > 4 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">More Suggestions</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {suggestions.slice(4).map(item => (
              <div 
                key={item.id}
                className={`bg-white rounded-lg p-3 hover:shadow-md transition-all duration-300 ${
                  animatingIds.includes(item.id) ? 'scale-95 opacity-50' : 'scale-100 opacity-100'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <img 
                    src={item.image} 
                    alt={item.name} 
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                  <div className="flex-1">
                    <h5 className="text-xs font-medium text-gray-800 line-clamp-1">
                      {item.name}
                    </h5>
                    <p className="text-sm font-bold text-gray-900 mt-1">
                      {formatCurrency(item.price)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleAddToCart(item)}
                    className="p-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PharmacySelector = ({ pharmacies, selectedPharmacy, cart, onSelect, userLocation }) => {
  const checkStock = useCallback((pharmacy, cart) => {
    if (!cart || cart.length === 0) {
      return { availableItems: 0, totalItems: 0, hasAllItems: true, unavailableItems: [] };
    }
    let availableItems = 0;
    const unavailableItems = [];
    cart.forEach(item => {
      if (pharmacy.stock[item.id] && pharmacy.stock[item.id] >= item.quantity) {
        availableItems++;
      } else {
        unavailableItems.push(item.name);
      }
    });
    return {
      availableItems,
      totalItems: cart.length,
      hasAllItems: availableItems === cart.length,
      unavailableItems,
    };
  }, []);

  const selectBestMatch = useCallback(() => {
    const bestPharmacy = pharmacies
      .map(p => ({
        ...p,
        distance: parseFloat(getDistanceFromLatLonInKm(userLocation.lat, userLocation.lng, p.location.lat, p.location.lng))
      }))
      .filter(p => checkStock(p, cart).hasAllItems)
      .sort((a, b) => a.distance - b.distance)[0];
    
    if (bestPharmacy) {
      onSelect(bestPharmacy);
    } else {
      const bestFallback = pharmacies
        .map(p => ({ 
          ...p, 
          stockInfo: checkStock(p, cart),
          distance: parseFloat(getDistanceFromLatLonInKm(userLocation.lat, userLocation.lng, p.location.lat, p.location.lng))
        }))
        .sort((a, b) => {
          if (b.stockInfo.availableItems !== a.stockInfo.availableItems) {
            return b.stockInfo.availableItems - a.stockInfo.availableItems;
          }
          return a.distance - b.distance;
        })[0];
      onSelect(bestFallback);
    }
  }, [pharmacies, cart, userLocation, checkStock, onSelect]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold">Choose a Pharmacy</h3>
        <button 
          onClick={selectBestMatch}
          className="text-sm bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg font-medium hover:bg-blue-200 transition-colors flex items-center"
        >
          <Star className="h-4 w-4 mr-1.5" />
          Select Best Match
        </button>
      </div>
      <div className="space-y-3">
        {pharmacies.map(pharmacy => {
          const stockInfo = checkStock(pharmacy, cart);
          const distance = getDistanceFromLatLonInKm(userLocation.lat, userLocation.lng, pharmacy.location.lat, pharmacy.location.lng);
          
          return (
            <div
              key={pharmacy.id}
              onClick={() => onSelect(pharmacy)}
              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                selectedPharmacy?.id === pharmacy.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start">
                <div className="w-10 h-10 bg-gray-100 rounded-lg mr-3 flex items-center justify-center overflow-hidden">
                  <img src={pharmacy.image} alt={pharmacy.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <h4 className="font-medium">{pharmacy.name}</h4>
                    <div className="flex items-center text-sm">
                      <MapPin className="h-4 w-4 text-gray-500 mr-1" />
                      <span>{distance} km</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600">{pharmacy.address}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-xs text-gray-500">Rating: {pharmacy.rating}/5</span>
                    <span className="text-xs text-gray-500">• {pharmacy.openingHours}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {pharmacy.services.map(service => (
                      <span key={service} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                        {service}
                      </span>
                    ))}
                  </div>
                  {stockInfo.hasAllItems ? (
                    <div className="mt-2 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-md inline-flex items-center">
                      <CheckCircle className="h-3 w-3 mr-1.5" />
                      Has all {stockInfo.totalItems} items in stock
                    </div>
                  ) : (
                    <div className="mt-2 text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded-md inline-flex items-center" title={`Unavailable: ${stockInfo.unavailableItems.join(', ')}`}>
                      <AlertCircle className="h-3 w-3 mr-1.5" />
                      {stockInfo.availableItems} of {stockInfo.totalItems} items available
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Tab Components
const HomeTab = ({ state, dispatch }) => {
  const { location } = useGeolocation();
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 rounded-xl shadow-md">
        <h1 className="text-2xl font-bold mb-2">Welcome back, {state.user.name.split(' ')[0]}!</h1>
        <p className="opacity-90 mb-4">Your trusted medication pickup service.</p>
        <div className="flex space-x-2">
          <button 
            onClick={() => dispatch({ type: 'SET_ACTIVE_TAB', payload: 'prescriptions' })}
            className="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
          >
            My Prescriptions
          </button>
          <button 
            onClick={() => dispatch({ type: 'SET_ACTIVE_TAB', payload: 'cart' })}
            className="border border-white text-white px-4 py-2 rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
          >
            Start Order
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
        <h2 className="text-lg font-semibold mb-3 flex items-center">
          <Shield className="h-5 w-5 mr-2 text-blue-600" />
          PBS Safety Net Status
        </h2>
        
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-800 mb-2">
            Your current PBS Safety Net spend for 2025:
          </p>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium">{formatCurrency(state.user.pbsSafetyNet.currentSpend)}</span>
            <span className="text-sm font-medium">{formatCurrency(state.user.pbsSafetyNet.threshold)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-500" 
              style={{ width: `${state.user.pbsSafetyNet.percentage}%` }}
            ></div>
          </div>
          <p className="text-xs text-blue-700 mt-2">
            {formatCurrency(state.user.pbsSafetyNet.remainingToThreshold)} more to reach Safety Net threshold
          </p>
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center">
          <Gift className="h-5 w-5 mr-2" />
          Special Offers
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <Zap className="h-6 w-6 mr-2" />
              <h3 className="font-semibold">Quick Pickup Available!</h3>
            </div>
            <p className="text-sm opacity-90">Ready in 30-60 minutes</p>
          </div>
          <div className="bg-gradient-to-r from-green-500 to-teal-500 text-white p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <Award className="h-6 w-6 mr-2" />
              <h3 className="font-semibold">Loyalty Points</h3>
            </div>
            <p className="text-sm opacity-90">Earn 2x points this week</p>
          </div>
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            Health Tips
          </h2>
          <button className="text-sm text-blue-600">View All</button>
        </div>
        <div className="space-y-3">
          {healthTips.map(tip => (
            <div key={tip.id} className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start">
                <div className="bg-gray-100 rounded-full p-2 mr-3">
                  <tip.icon className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <h3 className="font-medium">{tip.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{tip.content}</p>
                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full mt-2 inline-block">
                    {tip.category}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SearchTab = ({ state, dispatch }) => {
  // Get prescribed medications only
  const prescribedMedications = useMemo(() => {
    return prescriptions.map(rx => {
      const med = medications.find(m => m.name === rx.medication);
      return med ? { ...med, prescription: rx } : null;
    }).filter(Boolean);
  }, []);

  if (prescribedMedications.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-bold">My Prescribed Medications</h2>
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-gray-200">
          <Pill className="h-20 w-20 text-gray-300 mb-4" />
          <h3 className="text-xl font-medium text-gray-700 mb-2">No Active Prescriptions</h3>
          <p className="text-gray-500 mb-6 text-center px-4">
            Your prescribed medications will appear here when your doctor sends an eScript
          </p>
          <button 
            onClick={() => dispatch({ type: 'SET_ACTIVE_TAB', payload: 'prescriptions' })}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            View Prescription History
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold mb-2">My Prescribed Medications</h2>
        <p className="text-sm text-gray-600">Your prescribed medications available for refill</p>
      </div>

      <div className="space-y-4">
        {prescribedMedications.map(med => (
          <div key={med.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex">
              <div className="w-16 h-16 bg-gray-100 rounded-lg mr-3 flex items-center justify-center overflow-hidden">
                <img src={med.image} alt={med.name} className="w-full h-full object-cover" />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-semibold text-gray-900">{med.name}</h3>
                  <button
                    onClick={() => dispatch({ type: 'TOGGLE_FAVORITE', payload: med.id })}
                    className={`p-1 rounded-full hover:bg-gray-100 ${
                      state.favorites.includes(med.id) ? 'text-red-500' : 'text-gray-400'
                    }`}
                  >
                    <Heart className={`h-5 w-5 ${state.favorites.includes(med.id) ? 'fill-current' : ''}`} />
                  </button>
                </div>
                
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    {med.category}
                  </span>
                  <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                    Prescription
                  </span>
                  {med.pbs && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      PBS
                    </span>
                  )}
                </div>
                
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>Prescribed by: {med.prescription.doctor}</span>
                  <span className={`font-medium ${
                    med.prescription.refillsRemaining > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {med.prescription.refillsRemaining} refills left
                  </span>
                </div>
              </div>
              
              <div className="text-right ml-4">
                <div className="mb-2">
                  {med.pbs && med.pbsPrice ? (
                    <>
                      <p className="text-lg font-bold text-gray-900">{formatCurrency(med.pbsPrice)}</p>
                      <p className="text-xs text-gray-500 line-through">RRP {formatCurrency(med.price)}</p>
                      <p className="text-xs text-green-600">PBS Price</p>
                    </>
                  ) : (
                    <p className="text-lg font-bold text-gray-900">{formatCurrency(med.price)}</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex space-x-2">
              <button
                onClick={() => dispatch({ type: 'ADD_TO_CART', payload: med })}
                disabled={med.prescription.refillsRemaining === 0}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  med.prescription.refillsRemaining > 0
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {med.prescription.refillsRemaining > 0 ? 'Add to Cart' : 'No Refills Available'}
              </button>
              <button className="py-2 px-3 border border-gray-300 rounded-lg hover:bg-gray-50">
                <History className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            
            {med.prescription.status === 'Expiring Soon' && (
              <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded flex items-center text-sm text-orange-800">
                <AlertCircle className="h-4 w-4 mr-2" />
                Prescription expires on {med.prescription.expires}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const CartTab = ({ state, dispatch }) => {
  const { location } = useGeolocation();
  const [pickupMethod, setPickupMethod] = useState('self');
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  
  const subtotal = useMemo(() => {
    return state.cart.reduce((total, item) => {
      const price = item.pbs && item.pbsPrice ? item.pbsPrice : item.price;
      return total + (price * item.quantity);
    }, 0);
  }, [state.cart]);
  
  const gst = subtotal * 0.10;
  const total = subtotal + gst;

  const handleCheckout = () => {
    // Process payment
    const orderData = {
      items: state.cart,
      pharmacy: state.selectedPharmacy,
      pickupMethod: pickupMethod,
      paymentMethod,
      total,
      timestamp: new Date().toISOString()
    };
    
    // Add success notification
    dispatch({
      type: 'ADD_NOTIFICATION',
      payload: {
        id: Date.now(),
        type: 'ready',
        title: 'Order Confirmed',
        message: `Your order will be ready for pickup soon`,
        time: 'Just now',
        read: false
      }
    });
    
    // Clear cart and reset
    dispatch({ type: 'CLEAR_CART' });
    dispatch({ type: 'SET_SELECTED_PHARMACY', payload: null });
    dispatch({ type: 'SET_ACTIVE_TAB', payload: 'home' });
    setShowCheckout(false);
  };

  if (state.cart.length === 0) {
    return (
      <div className="space-y-4 pb-20">
        <h2 className="text-xl font-bold">Your Cart</h2>
        
        <div className="flex flex-col items-center justify-center py-12 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-blue-200">
          <ShoppingCart className="h-20 w-20 text-blue-300 mb-4" />
          <h3 className="text-xl font-medium text-gray-700 mb-2">Your cart is empty</h3>
          <p className="text-gray-500 mb-6">Add prescribed medications from your prescriptions</p>
          <button 
            onClick={() => dispatch({ type: 'SET_ACTIVE_TAB', payload: 'prescriptions' })}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 shadow-md"
          >
            View My Prescriptions
          </button>
        </div>

        <PharmacySelector
          pharmacies={pharmacies}
          selectedPharmacy={state.selectedPharmacy}
          cart={[]}
          onSelect={(pharmacy) => dispatch({ type: 'SET_SELECTED_PHARMACY', payload: pharmacy })}
          userLocation={location}
        />

        {state.selectedPharmacy && (
          <ComplementaryProductsSection
            pharmacy={state.selectedPharmacy}
            userProfile={state.user}
            dispatch={dispatch}
            cartItems={state.cart}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20">
      <h2 className="text-xl font-bold">Your Cart</h2>
      
      <div className="space-y-3">
        {state.cart.map(item => (
          <div key={item.id} className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm">
            <div className="flex justify-between items-start mb-3">
              <div className="flex">
                <div className="w-12 h-12 bg-gray-100 rounded-lg mr-3 flex items-center justify-center overflow-hidden">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="font-semibold">{item.name}</h3>
                  {item.brandName && (
                    <p className="text-xs text-gray-500">Brand: {item.brandName}</p>
                  )}
                  <div className="flex items-center space-x-1 mt-1">
                    {item.prescription && (
                      <span className="text-xs bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded-full">
                        Rx
                      </span>
                    )}
                    {item.pbs && (
                      <span className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">
                        PBS
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                {item.pbs && item.pbsPrice ? (
                  <>
                    <p className="font-bold">{formatCurrency(item.pbsPrice * item.quantity)}</p>
                    <p className="text-xs text-gray-500 line-through">
                      {formatCurrency(item.price * item.quantity)}
                    </p>
                    <p className="text-xs text-green-600">PBS Price</p>
                  </>
                ) : (
                  <p className="font-bold">{formatCurrency(item.price * item.quantity)}</p>
                )}
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => dispatch({ 
                    type: 'UPDATE_CART_QUANTITY', 
                    payload: { id: item.id, quantity: item.quantity - 1 }
                  })}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="font-medium">{item.quantity}</span>
                <button
                  onClick={() => dispatch({ 
                    type: 'UPDATE_CART_QUANTITY', 
                    payload: { id: item.id, quantity: item.quantity + 1 }
                  })}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              
              <button
                onClick={() => dispatch({ type: 'REMOVE_FROM_CART', payload: item.id })}
                className="text-red-600 text-sm hover:text-red-700 flex items-center"
              >
                <X className="h-4 w-4 mr-1" />
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <PharmacySelector
        pharmacies={pharmacies}
        selectedPharmacy={state.selectedPharmacy}
        cart={state.cart}
        onSelect={(pharmacy) => dispatch({ type: 'SET_SELECTED_PHARMACY', payload: pharmacy })}
        userLocation={location}
      />

      {state.selectedPharmacy && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <div>
              <p className="font-medium text-green-800">Pharmacy Selected: {state.selectedPharmacy.name}</p>
              <p className="text-sm text-green-700">Estimated pickup time: 30-60 minutes after order confirmation</p>
            </div>
          </div>
        </div>
      )}

      {state.selectedPharmacy && (
        <ComplementaryProductsSection
          pharmacy={state.selectedPharmacy}
          userProfile={state.user}
          dispatch={dispatch}
          cartItems={state.cart}
        />
      )}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h3 className="font-semibold mb-3">Order Summary</h3>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Subtotal</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>GST (10%)</span>
            <span>{formatCurrency(gst)}</span>
          </div>
          <div className="flex justify-between font-semibold text-base border-t pt-2 mt-2">
            <span>Total</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>
        
        <button
          disabled={!state.selectedPharmacy}
          onClick={() => setShowCheckout(true)}
          className={`w-full mt-4 py-3 px-4 rounded-lg font-medium transition-colors ${
            state.selectedPharmacy
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Proceed to Checkout
        </button>
      </div>
      
      {showCheckout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-lg font-semibold">Checkout</h2>
              <button onClick={() => setShowCheckout(false)}>
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <h3 className="font-medium mb-3">Pickup Method</h3>
                <div className="flex rounded-lg border border-gray-200 p-1 bg-gray-100">
                  <button 
                    onClick={() => setPickupMethod('self')}
                    className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                      pickupMethod === 'self' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
                    }`}
                  >
                    Self Pickup
                  </button>
                  <button 
                    onClick={() => setPickupMethod('authorized')}
                    className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                      pickupMethod === 'authorized' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
                    }`}
                  >
                    Authorized Pickup
                  </button>
                </div>
                {pickupMethod === 'authorized' && (
                  <p className="text-xs text-gray-600 mt-2">
                    A QR code will be sent to your email for the authorized person to show at pickup.
                  </p>
                )}
              </div>
              
              <div>
                <h3 className="font-medium mb-3">Payment Method</h3>
                <div className="space-y-2">
                  <label className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                    paymentMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="payment"
                      value="card"
                      checked={paymentMethod === 'card'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <p className="font-medium">Credit/Debit Card</p>
                      <p className="text-sm text-gray-600">Visa ending in ****4321</p>
                    </div>
                    <CreditCard className="h-5 w-5 text-gray-400" />
                  </label>
                  
                  <label className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                    paymentMethod === 'paypal' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="payment"
                      value="paypal"
                      checked={paymentMethod === 'paypal'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <p className="font-medium">PayPal</p>
                      <p className="text-sm text-gray-600">sarah.johnson@email.com</p>
                    </div>
                  </label>
                  
                  <label className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                    paymentMethod === 'store' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}>
                    <input
                      type="radio"
                      name="payment"
                      value="store"
                      checked={paymentMethod === 'store'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <p className="font-medium">Pay at Store</p>
                      <p className="text-sm text-gray-600">Cash or card on pickup</p>
                    </div>
                  </label>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium mb-3">Order Total</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>GST (10%)</span>
                    <span>{formatCurrency(gst)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowCheckout(false)}
                  className="flex-1 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCheckout}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                >
                  Confirm Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PrescriptionsTab = ({ state, dispatch }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">My Prescriptions</h2>
        <button 
          onClick={() => setShowAddModal(true)}
          className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg flex items-center"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add New
        </button>
      </div>

      <div className="bg-white border border-blue-200 rounded-lg p-4 bg-blue-50">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-600 mt-1 mr-3 flex-shrink-0" />
          <div>
            <p className="text-sm text-blue-800">Your prescriptions are securely stored and verified with the TGA (Therapeutic Goods Administration) database.</p>
            <p className="text-xs text-blue-600 mt-1">We'll notify you when prescriptions are about to expire.</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {prescriptions.map(rx => (
          <div key={rx.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-lg">{rx.medication}</h3>
                <p className="text-sm text-gray-600">Prescribed by {rx.doctor}</p>
                <p className="text-sm text-gray-500">{rx.id}</p>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                rx.status === 'Active' ? 'bg-green-100 text-green-800' :
                rx.status === 'Expiring Soon' ? 'bg-orange-100 text-orange-800' :
                'bg-red-100 text-red-800'
              }`}>
                {rx.status}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
              <div>
                <p className="text-gray-600">Issued: <span className="font-medium">{rx.issued}</span></p>
                <p className="text-gray-600">Expires: <span className="font-medium">{rx.expires}</span></p>
              </div>
              <div>
                <p className="text-gray-600">Refills: <span className="font-medium">{rx.refillsRemaining}/{rx.totalRefills}</span></p>
                <p className="text-gray-600">PBS Listed: <span className="font-medium text-green-600">{rx.pbs ? 'Yes' : 'No'}</span></p>
              </div>
            </div>

            <div className="flex space-x-2">
              <button 
                disabled={rx.refillsRemaining === 0}
                onClick={() => {
                  const medication = medications.find(m => m.name === rx.medication);
                  if (medication) {
                    dispatch({ type: 'ADD_TO_CART', payload: medication });
                    dispatch({ type: 'SET_ACTIVE_TAB', payload: 'cart' });
                  }
                }}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  rx.refillsRemaining > 0 
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Order Refill
              </button>
              <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <Calendar className="h-4 w-4" />
              </button>
              <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <History className="h-4 w-4" />
              </button>
            </div>

            {rx.isRepeat && (
              <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded flex items-center text-sm text-blue-800">
                <RefreshCw className="h-4 w-4 mr-2" />
                Auto-refill enabled - next reminder on {rx.nextReminderDate}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold">Add Prescription</h2>
              <button onClick={() => setShowAddModal(false)}>
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div className="text-center py-8">
                  <Upload className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Upload eScript</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload a photo of your prescription or eScript QR code
                  </p>
                  <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
                    <Camera className="h-5 w-5 inline mr-2" />
                    Take Photo
                  </button>
                </div>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">or</span>
                  </div>
                </div>
                
                <button className="w-full py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  Enter Prescription Details Manually
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FavoritesTab = ({ state, dispatch }) => {
  const favoriteMedications = medications.filter(med => state.favorites.includes(med.id));
  
  if (favoriteMedications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Heart className="h-20 w-20 text-gray-300 mb-4" />
        <h3 className="text-xl font-medium text-gray-700 mb-2">No favorites yet</h3>
        <p className="text-gray-500 mb-6">Save your frequently ordered medications here</p>
        <button 
          onClick={() => dispatch({ type: 'SET_ACTIVE_TAB', payload: 'search' })}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
        >
          View My Medications
        </button>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">My Favorites</h2>
      <p className="text-sm text-gray-600">Your saved medications for quick reordering</p>
      
      <div className="space-y-4">
        {favoriteMedications.map(med => (
          <MedicationCard 
            key={med.id} 
            medication={med} 
            dispatch={dispatch}
            isFavorite={true}
          />
        ))}
      </div>
    </div>
  );
};

const OrdersTab = ({ state, dispatch }) => {
  // Mock orders data
  const orders = [
    {
      id: 'ORD001',
      date: '2025-01-20',
      status: 'Collected',
      total: 45.99,
      items: 3,
      pharmacy: 'MediCare Pharmacy',
      pickupMethod: 'Self Pickup'
    },
    {
      id: 'ORD002',
      date: '2025-01-15',
      status: 'Ready for Pickup',
      total: 28.50,
      items: 2,
      pharmacy: 'Central City Pharmacy',
      pickupMethod: 'Authorized Pickup',
      qrCodeSent: true
    }
  ];
  
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Order History</h2>
      
      <div className="space-y-3">
        {orders.map(order => (
          <div key={order.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="font-medium">Order #{order.id}</p>
                <p className="text-sm text-gray-600">{order.date}</p>
                <p className="text-xs text-gray-500">{order.pharmacy}</p>
                <p className="text-xs text-gray-500 mt-1">{order.pickupMethod}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold">{formatCurrency(order.total)}</p>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  order.status === 'Collected' ? 'bg-green-100 text-green-800' :
                  order.status === 'Ready for Pickup' ? 'bg-blue-100 text-blue-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {order.status}
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">{order.items} items</p>
              <div className="flex items-center space-x-2">
                {order.qrCodeSent && (
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full flex items-center">
                    <QrCode className="h-3 w-3 mr-1" />
                    QR Sent
                  </span>
                )}
                <button className="text-sm text-blue-600 hover:text-blue-700">
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {orders.length === 0 && (
        <div className="text-center py-8 bg-white border border-gray-200 rounded-lg">
          <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-1">No orders yet</h3>
          <p className="text-gray-500">Your order history will appear here</p>
        </div>
      )}
    </div>
  );
};

const ProfileTab = ({ state, dispatch }) => {
  const [editMode, setEditMode] = useState(false);
  
  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 text-center">
        <div className="w-20 h-20 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
          <span className="text-2xl font-bold text-blue-600">
            {state.user.name.split(' ').map(n => n[0]).join('')}
          </span>
        </div>
        <h2 className="text-xl font-bold">{state.user.name}</h2>
        <p className="text-gray-600">{state.user.email}</p>
        <div className="mt-2 flex justify-center space-x-2">
          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
            Retail Customer
          </span>
          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
            PBS Eligible
          </span>
        </div>
        
        <button 
          onClick={() => setEditMode(!editMode)}
          className="mt-4 text-sm text-blue-600 hover:text-blue-700 flex items-center justify-center mx-auto"
        >
          <Edit className="h-4 w-4 mr-1" />
          Edit Profile
        </button>
        
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500">Medicare</p>
            <p className="font-medium">{state.user.medicare}</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500">Concession Card</p>
            <p className="font-medium">{state.user.concessionCard}</p>
          </div>
        </div>
      </div>
      
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 bg-blue-50 border-b border-blue-200">
          <h3 className="font-medium text-blue-800">PBS Safety Net Progress</h3>
        </div>
        <div className="p-4">
          <div className="flex justify-between text-sm mb-2">
            <span>Current Spend</span>
            <span>Threshold</span>
          </div>
          <div className="flex justify-between font-medium mb-2">
            <span>{formatCurrency(state.user.pbsSafetyNet.currentSpend)}</span>
            <span>{formatCurrency(state.user.pbsSafetyNet.threshold)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
            <div 
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-500" 
              style={{ width: `${state.user.pbsSafetyNet.percentage}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600">
            {formatCurrency(state.user.pbsSafetyNet.remainingToThreshold)} more to reach Safety Net threshold
          </p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-medium">Account Settings</h3>
        </div>
        <div>
          {[
            { icon: Package, label: 'Order History', badge: '12', desc: 'View past orders and tracking' },
            { icon: FileText, label: 'Prescription Management', badge: prescriptions.length.toString(), desc: 'Active prescriptions and refills' },
            { icon: Heart, label: 'Favorites', badge: state.favorites.length.toString(), desc: 'Saved medications' },
            { icon: CreditCard, label: 'Payment Methods', badge: null, desc: 'Manage payment options' },
            { icon: MapPin, label: 'Preferred Pharmacies', badge: null, desc: 'Save your favorite locations' },
            { icon: Bell, label: 'Notifications', badge: null, desc: 'SMS and app notifications' },
            { icon: Shield, label: 'Privacy & Security', badge: null, desc: 'Account security settings' },
            { icon: Phone, label: 'Help & Support', badge: null, desc: '24/7 pharmacy assistance' }
          ].map((item, index) => (
            <div 
              key={index} 
              className="p-4 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-0"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="bg-gray-100 rounded-full p-2 mr-3">
                    <item.icon className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium">{item.label}</p>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  {item.badge && (
                    <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-2">
                      {item.badge}
                    </span>
                  )}
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
            <Shield className="h-4 w-4 mr-2" />
            Insurance Information
          </h3>
          <p className="text-sm text-blue-700">{state.user.insurance} - Plan verified ✓</p>
          <p className="text-xs text-blue-600 mt-1">PBS benefits active • TGA verification enabled</p>
          <button className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
            Update Insurance
          </button>
        </div>
      </div>
      
      <button className="w-full py-3 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center">
        <LogOut className="h-5 w-5 mr-2" />
        Sign Out
      </button>
    </div>
  );
};

// Main App Component
const MedFastEnhanced = () => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Initialize notifications
  useEffect(() => {
    const initialNotifications = [
      { 
        id: 1, 
        type: 'sms', 
        title: 'New eScript received',
        message: 'Metformin 500mg refill is ready to order',
        time: '2 min ago', 
        read: false,
        prescriptionId: 'RX001',
        action: 'Order now'
      },
      { 
        id: 2, 
        type: 'reminder', 
        title: 'Prescription expiring soon',
        message: 'Lisinopril 10mg expires in 3 days',
        time: '1 hour ago', 
        read: false,
        prescriptionId: 'RX002',
        action: 'Renew now'
      }
    ];

    initialNotifications.forEach(notification => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
    });
  }, []);

  const renderActiveTab = () => {
    switch (state.activeTab) {
      case 'home':
        return <HomeTab state={state} dispatch={dispatch} />;
      case 'search':
        return <SearchTab state={state} dispatch={dispatch} />;
      case 'cart':
        return <CartTab state={state} dispatch={dispatch} />;
      case 'prescriptions':
        return <PrescriptionsTab state={state} dispatch={dispatch} />;
      case 'favorites':
        return <FavoritesTab state={state} dispatch={dispatch} />;
      case 'orders':
        return <OrdersTab state={state} dispatch={dispatch} />;
      case 'profile':
        return <ProfileTab state={state} dispatch={dispatch} />;
      default:
        return <HomeTab state={state} dispatch={dispatch} />;
    }
  };

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      <div className="max-w-md mx-auto bg-gray-50 min-h-screen font-sans">
        <Header state={state} dispatch={dispatch} />
        
        <div className="p-4 pb-20">
          {renderActiveTab()}
        </div>
        
        <BottomNavigation activeTab={state.activeTab} dispatch={dispatch} />
      </div>
    </AppContext.Provider>
  );
};

export default MedFastEnhanced;